import { memo, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { FixedSizeList as List } from 'react-window'
import { useNotes } from '../../hooks/useNotes'
import { useTagsStore } from '../../store/tagsStore'
import { NoteListItem } from './NoteListItem'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Plus, Search } from 'lucide-react'
import { cn } from '../../lib/utils'

const ITEM_HEIGHT = 100 // Reduced height for sidebar

interface NoteListProps {
  className?: string
}

export interface NoteListRef {
  focusSearch: () => void
}

// Memoized list item component for react-window
const ListItem = memo(({ index, style, data }: any) => {
  const { notes, selectedNoteId, selectNote, getTagsForNote, deleteNote } = data
  const note = notes[index]
  const [noteTags, setNoteTags] = useState<any[]>([])

  useEffect(() => {
    const loadTags = async () => {
      const tags = await getTagsForNote(note.id)
      setNoteTags(tags)
    }
    loadTags()
  }, [note.id, getTagsForNote])

  return (
    <div style={style}>
      <NoteListItem
        note={note}
        isSelected={note.id === selectedNoteId}
        onClick={() => selectNote(note.id)}
        onDelete={deleteNote}
        tags={noteTags}
        compact={true} // Add compact mode for sidebar
      />
    </div>
  )
})

export const NoteList = memo(forwardRef<NoteListRef, NoteListProps>(({ className }, ref) => {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const {
    notes,
    selectedNoteId,
    isLoading,
    error,
    searchQuery,
    createNote,
    selectNote,
    deleteNote,
    setSearchQuery,
    clearError,
    hasNotes
  } = useNotes()

  const { getTagsForNote } = useTagsStore()

  // Expose search focus method via ref
  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      searchInputRef.current?.focus()
    }
  }), [])

  const handleCreateNote = async () => {
    const note = await createNote()
    if (note) {
      selectNote(note.id)
    }
  }

  const listData = {
    notes,
    selectedNoteId,
    selectNote,
    deleteNote,
    getTagsForNote
  }

  if (error) {
    return (
      <div className={cn('flex flex-col', className)}>
        <div className="p-3 border-b">
          <div className="bg-destructive/10 text-destructive text-sm p-2 rounded-md">
            {error}
            <Button
              onClick={clearError}
              variant="ghost"
              size="sm"
              className="ml-2 h-auto p-1"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Compact Header */}
      <div className="flex-shrink-0 p-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium">Notes</h3>
          <Button onClick={handleCreateNote} size="sm" className="gap-1 h-7 px-2">
            <Plus className="w-3 h-3" />
            New
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 h-8 text-sm"
          />
        </div>

        {/* Notes count */}
        {hasNotes && (
          <div className="text-xs text-muted-foreground mt-1">
            {notes.length} {notes.length === 1 ? 'note' : 'notes'}
          </div>
        )}
      </div>

      {/* Notes list */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-xs text-muted-foreground">Loading...</div>
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            {searchQuery ? (
              <>
                <div className="text-xs text-muted-foreground mb-2">No notes found</div>
                <Button
                  onClick={() => setSearchQuery('')}
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                >
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-2">No notes yet</div>
                <Button onClick={handleCreateNote} size="sm" className="gap-1 h-7 text-xs">
                  <Plus className="w-3 h-3" />
                  Create Note
                </Button>
              </>
            )}
          </div>
        ) : (
          <List
            height={window.innerHeight - 50} // Adjust for sidebar layout
            width="100%"
            itemCount={notes.length}
            itemSize={ITEM_HEIGHT}
            itemData={listData}
            overscanCount={3}
          >
            {ListItem}
          </List>
        )}
      </div>
    </div>
  )
}))

NoteList.displayName = 'NoteList'
